(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),details>summary,[tabindex]:not([tabindex="-1"])';

  let lastCartFocus = null;
  let lastMenuFocus = null;
  let lastSearchFocus = null;

  function announce(message) {
    const toast = qs('#ThemeToast');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(window.__themeToastTimer);
    window.__themeToastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  }

  function focusable(container) {
    return qsa(focusableSelector, container).filter((element) => {
      if (element.tabIndex < 0 || element.closest('[aria-hidden="true"]') || element.getClientRects().length === 0) return false;
      const closedDetails = element.closest('details:not([open])');
      if (!closedDetails) return true;
      return element.matches('summary') && element.parentElement === closedDetails;
    });
  }

  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;
    const items = focusable(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setPageLocked(locked) {
    document.body.classList.toggle('drawer-open', locked);
  }

  function openCart() {
    const drawer = qs('#CartDrawer');
    if (!drawer) return;
    closeMenu(false);
    lastCartFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.inert = false;
    reconcilePageLock();
    focusable(drawer)[0]?.focus();
  }

  function closeCart(restoreFocus = true) {
    const drawer = qs('#CartDrawer');
    if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    reconcilePageLock();
    if (restoreFocus) lastCartFocus?.focus();
  }

  function openMenu(button) {
    const menu = qs('#MobileNav');
    if (!menu) return;
    closeCart(false);
    lastMenuFocus = button || document.activeElement;
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    menu.inert = false;
    button?.setAttribute('aria-expanded', 'true');
    reconcilePageLock();
    focusable(menu)[0]?.focus();
  }

  function closeMenu(restoreFocus = true) {
    const menu = qs('#MobileNav');
    if (!menu || menu.getAttribute('aria-hidden') === 'true') return;
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    menu.inert = true;
    qsa('[data-menu-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    reconcilePageLock();
    if (restoreFocus) lastMenuFocus?.focus();
  }

  function closeSearch(restoreFocus = false) {
    const search = qs('#HeaderSearch');
    const toggle = qs('[data-search-toggle]');
    if (!search || !toggle) return;
    const wasOpen = search.classList.contains('is-open');
    if (!wasOpen) return;
    search.classList.remove('is-open');
    search.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus) (lastSearchFocus || toggle).focus();
  }

  function reconcilePageLock() {
    const cartOpen = qs('#CartDrawer[aria-hidden="false"]');
    const menuOpen = qs('#MobileNav[aria-hidden="false"]');
    setPageLocked(Boolean(cartOpen || menuOpen));
    document.body.classList.toggle('mobile-menu-open', Boolean(menuOpen));
  }

  async function refreshCart(openAfterRefresh = false) {
    const drawerRequest = fetch(`${window.theme.routes.cart}?view=drawer`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('drawer');
        return response.text();
      });
    const cartRequest = fetch(`${window.theme.routes.cart}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then((response) => {
      if (!response.ok) throw new Error('cart');
      return response.json();
    });
    const [drawerResult, cartResult] = await Promise.allSettled([drawerRequest, cartRequest]);

    if (drawerResult.status === 'fulfilled') {
      const content = qs('#CartDrawerContent');
      if (content) content.innerHTML = drawerResult.value;
    }

    if (cartResult.status === 'fulfilled') {
      const cart = cartResult.value;
      qsa('[data-cart-count]').forEach((element) => {
        element.textContent = cart.item_count;
        element.hidden = cart.item_count === 0;
      });
      qsa('[data-cart-label]').forEach((element) => {
        const itemLabel = cart.item_count === 1 ? 'articol' : 'articole';
        element.setAttribute('aria-label', `Coș, ${cart.item_count} ${itemLabel}`);
      });
    }

    if (openAfterRefresh && drawerResult.status === 'fulfilled') openCart();
    if (drawerResult.status === 'rejected' || cartResult.status === 'rejected') {
      throw new Error('Coșul nu a putut fi actualizat. Încearcă din nou.');
    }
  }

  function activeGalleryIndex(gallery) {
    return qsa('[data-gallery-slide]', gallery).findIndex((slide) => slide.classList.contains('is-active'));
  }

  function activateGallery(gallery, requestedIndex) {
    if (!gallery) return;
    const slides = qsa('[data-gallery-slide]', gallery);
    const thumbs = qsa('[data-gallery-thumb]', gallery);
    if (!slides.length) return;

    const index = ((requestedIndex % slides.length) + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      if (!active) {
        qsa('video', slide).forEach((video) => video.pause());
        qsa('iframe', slide).forEach((frame) => {
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
          frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
        });
      }
    });
    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
    });

    const counter = qs('[data-gallery-counter]', gallery);
    if (counter) counter.textContent = `${index + 1} / ${slides.length}`;

    const section = gallery.closest('section') || gallery;
    const progress = qs('[data-gallery-progress]', section);
    if (progress) progress.style.width = `${((index + 1) / slides.length) * 100}%`;

    const zoomButton = qs('[data-gallery-zoom]', gallery);
    if (zoomButton) zoomButton.hidden = slides[index].dataset.galleryMediaType !== 'image';
  }

  function stepGallery(gallery, amount) {
    const current = Math.max(0, activeGalleryIndex(gallery));
    activateGallery(gallery, current + amount);
  }

  function openZoom(button) {
    const gallery = button.closest('[data-product-gallery]');
    const slide = qs('[data-gallery-slide].is-active', gallery);
    const sourceImage = slide?.querySelector('img');
    const dialogId = button.dataset.zoomDialog;
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    const content = dialog && qs('[data-zoom-content]', dialog);
    if (!sourceImage || !dialog || !content) return;

    const image = new Image();
    image.src = sourceImage.src || sourceImage.currentSrc;
    image.alt = sourceImage.alt || '';
    image.decoding = 'async';
    content.replaceChildren(image);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    qs('[data-zoom-close]', dialog)?.focus();
  }

  function closeZoom(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ro-RO', {
        style: 'currency',
        currency: window.theme.currency
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${window.theme.currency}`;
    }
  }

  function uniqueSelectedBundleItems(section) {
    const items = qsa('[data-bundle-item]', section).filter((input) => input.checked && !input.disabled);
    return [...new Map(items.map((input) => [input.value, input])).values()];
  }

  function updateBundle(section) {
    if (!section) return;
    const selected = uniqueSelectedBundleItems(section);
    const total = selected.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
    const count = qs('[data-bundle-count]', section);
    const countLabel = qs('[data-bundle-count-label]', section);
    const totalElement = qs('[data-bundle-total]', section);
    const button = qs('[data-bundle-add]', section);

    if (count) count.textContent = selected.length;
    if (countLabel) countLabel.textContent = selected.length === 1 ? section.dataset.countSingular : section.dataset.countPlural;
    if (totalElement) totalElement.textContent = formatMoney(total);
    if (button && !button.dataset.loading) button.disabled = selected.length === 0;
  }

  function updateProduct(root) {
    const json = qs('[data-product-json]', root);
    if (!json) return;

    let variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (error) {
      return;
    }

    const selects = qsa('[data-option-select]', root);
    const idInput = qs('form.product-form [name="id"]', root);
    const addButton = qs('[data-add-button]', root);
    const price = qs('[data-product-price]', root);
    const compare = qs('[data-product-compare]', root);
    const saving = qs('[data-product-saving]', root);
    const priceWrap = qs('[data-price-wrap]', root);
    const stickyPrice = qs('[data-sticky-price]', root);
    const stickyButton = qs('[data-sticky-submit]', root);
    const productForm = qs('form.product-form', root);

    const renderVariant = () => {
      const loading = productForm?.dataset.submitting === 'true';
      const options = selects.map((select) => select.value);
      const variant = selects.length
        ? variants.find((candidate) => candidate.options.every((option, index) => option === options[index]))
        : variants.find((candidate) => String(candidate.id) === String(idInput?.value)) || variants[0];

      if (!variant) {
        if (idInput) idInput.value = '';
        if (price) price.textContent = addButton?.dataset.unavailable || 'Indisponibil';
        if (stickyPrice) stickyPrice.textContent = addButton?.dataset.unavailable || 'Indisponibil';
        priceWrap?.classList.remove('price--sale');
        if (compare) {
          compare.hidden = true;
          compare.textContent = '';
        }
        if (saving) {
          saving.hidden = true;
          saving.textContent = '';
        }
        if (addButton) {
          addButton.disabled = true;
          addButton.dataset.variantAvailable = 'false';
          if (!loading) addButton.textContent = addButton.dataset.unavailable;
        }
        if (stickyButton) {
          stickyButton.disabled = true;
          if (!loading) stickyButton.textContent = addButton?.dataset.unavailable || 'Indisponibil';
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      priceWrap?.classList.toggle('price--sale', onSale);
      if (compare) {
        compare.hidden = !onSale;
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      }
      if (saving) {
        saving.hidden = !onSale;
        if (onSale) {
          const amount = variant.compare_at_price - variant.price;
          const percent = Math.round((amount * 100) / variant.compare_at_price);
          saving.textContent = `Economisești ${formatMoney(amount)} (${percent}%)`;
        }
      }

      if (addButton) {
        addButton.dataset.variantAvailable = String(variant.available);
        addButton.disabled = loading || !variant.available;
        if (!loading) addButton.textContent = variant.available ? addButton.dataset.available : addButton.dataset.soldout;
      }
      if (stickyButton) {
        stickyButton.disabled = loading || !variant.available;
        if (!loading) stickyButton.textContent = variant.available ? (stickyButton.dataset.available || addButton?.dataset.available) : (stickyButton.dataset.soldout || addButton?.dataset.soldout);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media?.id) qs(`[data-gallery-thumb="${variant.featured_media.id}"]`, root)?.click();
    };

    selects.forEach((select) => {
      if (select.dataset.variantInitialized) return;
      select.dataset.variantInitialized = 'true';
      select.addEventListener('change', renderVariant);
    });
    renderVariant();
  }

  function initGallery(gallery) {
    if (gallery.dataset.galleryInitialized) return;
    gallery.dataset.galleryInitialized = 'true';
    activateGallery(gallery, Math.max(0, activeGalleryIndex(gallery)));

    const area = qs('[data-gallery-touch-area]', gallery);
    if (!area) return;
    let startX = 0;
    let startY = 0;

    area.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      startX = touch.clientX;
      startY = touch.clientY;
    }, { passive: true });

    area.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const differenceX = touch.clientX - startX;
      const differenceY = touch.clientY - startY;
      if (Math.abs(differenceX) > 48 && Math.abs(differenceX) > Math.abs(differenceY)) {
        stepGallery(gallery, differenceX < 0 ? 1 : -1);
      }
    }, { passive: true });

    gallery.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.target.closest?.('a,button,input,select,textarea,video,iframe,model-viewer,[contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepGallery(gallery, -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepGallery(gallery, 1);
      }
    });
  }

  function initProductForm(form) {
    if (form.dataset.ajaxInitialized) return;
    form.dataset.ajaxInitialized = 'true';
    form.addEventListener('submit', async (event) => {
      if (!window.fetch || event.submitter?.name === 'checkout') return;
      event.preventDefault();
      if (form.dataset.submitting === 'true') return;
      const button = qs('[data-add-button]', form);
      if (button?.disabled) return;
      const initiatingFocus = document.activeElement;
      const root = form.closest('[data-product-root]');
      const stickyButton = root && qs('[data-sticky-submit]', root);
      form.dataset.submitting = 'true';

      if (button) {
        button.disabled = true;
        button.dataset.loading = 'true';
        button.textContent = 'Se adaugă…';
      }
      if (stickyButton) {
        stickyButton.disabled = true;
        stickyButton.textContent = 'Se adaugă…';
      }

      let added = false;
      try {
        const response = await fetch(`${window.theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Produsul nu a putut fi adăugat. Verifică disponibilitatea și încearcă din nou.');
        added = true;
        try {
          await refreshCart(true);
        } catch (refreshError) {
          announce('Produsul a fost adăugat. Reîncarcă pagina dacă actualizarea coșului nu apare imediat.');
        }
      } catch (error) {
        if (!added) announce('Produsul nu a putut fi adăugat. Verifică conexiunea și încearcă din nou.');
      } finally {
        if (button) delete button.dataset.loading;
        delete form.dataset.submitting;
        if (root) updateProduct(root);
        if (!qs('#CartDrawer[aria-hidden="false"]') && initiatingFocus?.isConnected) initiatingFocus.focus();
      }
    });
  }

  function syncStickyVisibility(sticky) {
    const visible = sticky.dataset.ctaPassed === 'true' && sticky.dataset.bundleVisible !== 'true';
    sticky.classList.toggle('is-visible', visible);
  }

  function initSticky(root) {
    const buyBox = qs('form.product-form', root) || qs('.product-action-panel', root);
    const sticky = qs('[data-sticky-buy]', root);
    if (!buyBox || !sticky || sticky.dataset.observerInitialized) return;
    sticky.dataset.observerInitialized = 'true';
    let frame = 0;
    const update = () => {
      frame = 0;
      const buyBoxRect = buyBox.getBoundingClientRect();
      const bundleVisible = qsa('[data-bundle-section]').some((section) => {
        const rect = section.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight;
      });
      sticky.dataset.ctaPassed = String(buyBoxRect.bottom <= 0);
      sticky.dataset.bundleVisible = String(bundleVisible);
      syncStickyVisibility(sticky);
    };
    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    sticky._themeScheduleSticky = scheduleUpdate;
    sticky._themeStickyCleanup = () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
    scheduleUpdate();
  }

  function refreshStickyState() {
    const sticky = qs('[data-sticky-buy]');
    sticky?._themeScheduleSticky?.();
  }

  function reconcileResponsiveOverlays() {
    if (!window.matchMedia('(max-width: 989px)').matches) {
      const menu = qs('#MobileNav');
      const focusWasInMenu = menu?.contains(document.activeElement);
      closeMenu(false);
      if (focusWasInMenu) qs('.header-logo')?.focus();
    }
    if (window.matchMedia('(max-width: 749px)').matches) {
      const search = qs('#HeaderSearch');
      const focusWasInSearch = search?.contains(document.activeElement);
      closeSearch(false);
      if (focusWasInSearch) qs('.header-logo')?.focus();
    }
  }

  function init(context = document) {
    qsa('[data-product-gallery]', context).forEach(initGallery);
    qsa('[data-bundle-section]', context).forEach(updateBundle);
    qsa('[data-product-root]', context).forEach((root) => {
      updateProduct(root);
      initSticky(root);
    });
    qsa('form.product-form', context).forEach(initProductForm);
    refreshStickyState();

    qsa('form.localization-switcher select', context).forEach((select) => {
      if (select.dataset.localizationInitialized) return;
      select.dataset.localizationInitialized = 'true';
      select.addEventListener('change', () => select.form.submit());
    });

    qsa('[data-sort-select]', context).forEach((select) => {
      if (select.dataset.sortInitialized) return;
      select.dataset.sortInitialized = 'true';
      select.addEventListener('change', () => select.form.submit());
    });

    const menu = qs('#MobileNav', context);
    if (menu) menu.inert = menu.getAttribute('aria-hidden') !== 'false';
    reconcilePageLock();
  }

  document.addEventListener('click', async (event) => {
    const menuOpen = event.target.closest('[data-menu-open]');
    const menuClose = event.target.closest('[data-menu-close]');
    const searchToggle = event.target.closest('[data-search-toggle]');
    const cartOpen = event.target.closest('[data-cart-open]');
    const cartClose = event.target.closest('[data-cart-close]');
    const thumb = event.target.closest('[data-gallery-thumb]');
    const galleryPrevious = event.target.closest('[data-gallery-prev]');
    const galleryNext = event.target.closest('[data-gallery-next]');
    const zoomOpen = event.target.closest('[data-gallery-zoom]');
    const zoomClose = event.target.closest('[data-zoom-close]');
    const stickySubmit = event.target.closest('[data-sticky-submit]');
    const quantityMinus = event.target.closest('[data-quantity-minus]');
    const quantityPlus = event.target.closest('[data-quantity-plus]');
    const bundleButton = event.target.closest('[data-bundle-add]');
    const desktopNavSummary = event.target.closest('.header-nav__summary');

    if (desktopNavSummary) {
      const currentItem = desktopNavSummary.closest('.header-nav__item');
      qsa('.header-nav__item[open]').forEach((item) => {
        if (item !== currentItem) item.removeAttribute('open');
      });
    } else if (!event.target.closest('.header-nav__item')) {
      qsa('.header-nav__item[open]').forEach((item) => item.removeAttribute('open'));
    }

    if (menuOpen) openMenu(menuOpen);
    if (menuClose) closeMenu();

    if (searchToggle) {
      const search = qs('#HeaderSearch');
      if (search) {
        const open = !search.classList.contains('is-open');
        if (open) lastSearchFocus = searchToggle;
        search.classList.toggle('is-open', open);
        search.setAttribute('aria-hidden', String(!open));
        searchToggle.setAttribute('aria-expanded', String(open));
        if (open) qs('input[type="search"]', search)?.focus();
      }
    } else if (!event.target.closest('#HeaderSearch')) {
      closeSearch(false);
    }

    if (cartOpen) {
      event.preventDefault();
      openCart();
    }
    if (cartClose) {
      event.preventDefault();
      closeCart();
    }
    if (thumb) {
      const gallery = thumb.closest('[data-product-gallery]');
      activateGallery(gallery, qsa('[data-gallery-thumb]', gallery).indexOf(thumb));
    }
    if (galleryPrevious) stepGallery(galleryPrevious.closest('[data-product-gallery]'), -1);
    if (galleryNext) stepGallery(galleryNext.closest('[data-product-gallery]'), 1);
    if (zoomOpen) openZoom(zoomOpen);
    if (zoomClose) closeZoom(zoomClose.closest('[data-zoom-dialog]'));

    if (stickySubmit) {
      const form = document.getElementById(stickySubmit.dataset.stickySubmit);
      if (form?.requestSubmit) form.requestSubmit();
      else form?.querySelector('[type="submit"]')?.click();
    }

    const quantityControl = (quantityMinus || quantityPlus)?.closest('.quantity-input');
    if (quantityControl) {
      const input = qs('input[type="number"]', quantityControl);
      if (input) {
        const minimum = Number(input.min || 1);
        const current = Number(input.value || minimum);
        input.value = Math.max(minimum, current + (quantityPlus ? 1 : -1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (bundleButton) {
      const section = bundleButton.closest('[data-bundle-section]');
      const selected = uniqueSelectedBundleItems(section);
      if (!selected.length || bundleButton.dataset.loading) return;
      const initiatingFocus = document.activeElement;
      const bundleItems = qsa('[data-bundle-item]', section);

      bundleButton.dataset.loading = 'true';
      bundleButton.disabled = true;
      bundleButton.textContent = bundleButton.dataset.loadingLabel || 'Se adaugă…';
      bundleItems.forEach((input) => {
        input.dataset.wasDisabled = String(input.disabled);
        input.disabled = true;
      });
      let added = false;
      try {
        const response = await fetch(`${window.theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: selected.map((input) => ({ id: Number(input.value), quantity: 1 })) })
        });
        if (!response.ok) throw new Error('Pachetul nu a putut fi adăugat. Verifică selecția și încearcă din nou.');
        added = true;
        try {
          await refreshCart(true);
        } catch (refreshError) {
          announce('Pachetul a fost adăugat. Reîncarcă pagina dacă actualizarea coșului nu apare imediat.');
        }
      } catch (error) {
        if (!added) announce('Pachetul nu a putut fi adăugat. Verifică conexiunea și încearcă din nou.');
      } finally {
        bundleItems.forEach((input) => {
          input.disabled = input.dataset.wasDisabled === 'true';
          delete input.dataset.wasDisabled;
        });
        delete bundleButton.dataset.loading;
        bundleButton.textContent = bundleButton.dataset.defaultLabel;
        updateBundle(section);
        if (!qs('#CartDrawer[aria-hidden="false"]') && initiatingFocus?.isConnected) initiatingFocus.focus();
      }
    }
  });

  document.addEventListener('change', (event) => {
    const bundleItem = event.target.closest('[data-bundle-item]');
    if (bundleItem) updateBundle(bundleItem.closest('[data-bundle-section]'));
  });

  document.addEventListener('keydown', (event) => {
    const drawer = qs('#CartDrawer[aria-hidden="false"]');
    const menu = qs('#MobileNav[aria-hidden="false"]');
    if (drawer) trapFocus(event, drawer);
    if (menu) trapFocus(event, menu);

    if (event.key === 'Escape') {
      if (drawer) closeCart();
      else if (menu) closeMenu();
      else closeSearch(true);
    }
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest('dialog[data-zoom-dialog]');
    if (dialog && event.target === dialog) closeZoom(dialog);
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    qs('[data-sticky-buy]', event.target)?._themeStickyCleanup?.();
    window.requestAnimationFrame(() => {
      reconcilePageLock();
      refreshStickyState();
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.matches?.('[data-gallery-slide]') ? event.target : qs('[data-gallery-slide]', event.target);
    const gallery = slide?.closest('[data-product-gallery]');
    if (!slide || !gallery) return;
    activateGallery(gallery, qsa('[data-gallery-slide]', gallery).indexOf(slide));
  });

  const cartDrawer = qs('#CartDrawer');
  const mobileMenu = qs('#MobileNav');
  if (cartDrawer) cartDrawer.inert = cartDrawer.getAttribute('aria-hidden') !== 'false';
  if (mobileMenu) mobileMenu.inert = mobileMenu.getAttribute('aria-hidden') !== 'false';
  let responsiveFrame = 0;
  window.addEventListener('resize', () => {
    if (responsiveFrame) return;
    responsiveFrame = window.requestAnimationFrame(() => {
      responsiveFrame = 0;
      reconcileResponsiveOverlays();
    });
  });
  init();
  reconcileResponsiveOverlays();
})();
